<script setup lang="ts">
  import { ref, onMounted, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import AdminSidebar from './AdminSidebar.vue';
  import AdminNavbar from './AdminNavbar.vue';
  import { ADMIN_MENU_ITEMS } from '../model/const';
  import { Footer } from '@/shared/ui/footer';
  import { NotificationCenterModal } from '@/shared/ui/notification-center';
  import { Breadcrumb } from '@/shared/ui';
  import { useUserStore } from '@/entities/User';
  import { useReminderHistoryStore } from '@/entities/ReminderHistory';
  import { useAuthStore } from '@/entities/Session';
  import { usePermission } from '@/entities/Permission';
  import { useCustomToast } from '@/shared/lib';
  import { CaslAction, CaslSubject } from '@/shared/lib/casl';

  const isCollapsed = ref(false);
  const selectedNavigation = ref('');
  const isNotificationModalOpen = ref(false);

  const toast = useCustomToast();

  const userStore = useUserStore();

  const { can } = usePermission();
  const canCheck = (action: string, subject: string) =>
    can(action as CaslAction, subject as CaslSubject).value;

  const reminderHistoryStore = useReminderHistoryStore();
  const {
    unreadNoticeCount,
    recentNotices,
    isSummaryLoading: isNoticesLoading,
    processingNoticeId,
  } = storeToRefs(reminderHistoryStore);
  const { loadUnreadNoticeCount, loadNoticeSummary, acknowledgeNotice, clearNotices } =
    reminderHistoryStore;

  const authStore = useAuthStore();
  const { selectedBranchId } = storeToRefs(authStore);

  onMounted(async () => {
    try {
      await loadUnreadNoticeCount();
    } catch {
      // Silently handle counter fetch errors - non-critical UI data
    }
  });

  watch(selectedBranchId, (newId, oldId) => {
    if (newId !== null && newId !== oldId) {
      clearNotices();
      void loadUnreadNoticeCount();
    }
  });

  const toggleSidebar = () => {
    isCollapsed.value = !isCollapsed.value;
  };

  const handleOpenNotification = () => {
    isNotificationModalOpen.value = true;
    void loadNoticeSummary();
  };

  const handleCloseNotification = () => {
    isNotificationModalOpen.value = false;
  };

  const handleAcknowledgeNotice = async (noticeId: number) => {
    if (processingNoticeId.value !== null) {
      return;
    }

    try {
      const didAcknowledge = await acknowledgeNotice(noticeId);

      if (!didAcknowledge) {
        return;
      }
    } catch {
      toast.showErrorToast({
        title: 'Erro ao confirmar ciência do aviso',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    }
  };

  const handleExpand = () => {
    if (isCollapsed.value) {
      toggleSidebar();
    }
  };

  const handleNavigationUpdate = (value: string) => {
    selectedNavigation.value = value;
  };
</script>

<template>
  <div class="flex h-screen overflow-hidden">
    <div
      :class="[
        isCollapsed ? 'w-16' : 'w-60',
        'border-r border-[var(--theme-secondary-ink)] transition-all duration-300 flex-shrink-0 bg-[var(--theme-primary)]',
        'h-screen fixed left-0 top-0 overflow-y-auto',
      ]"
    >
      <AdminSidebar
        data-testid="admin-sidebar"
        :is-collapsed="isCollapsed"
        :user-name="userStore.name || ''"
        :user-email="userStore.user?.email || ''"
        :admin-menu-items="ADMIN_MENU_ITEMS"
        :can-check="canCheck"
        @expand="handleExpand"
      />
    </div>

    <div
      :class="[
        isCollapsed ? 'ml-16' : 'ml-60',
        'w-full flex flex-col transition-all duration-300 overflow-auto',
      ]"
    >
      <div class="h-20 border-b border-[var(--theme-secondary-ink)] text-[var(--theme-text)] flex">
        <AdminNavbar
          data-testid="admin-navbar"
          :is-collapsed="isCollapsed"
          :branches="userStore.branches"
          :selected-navigation="selectedNavigation"
          :unread-notice-count="unreadNoticeCount"
          :can-check="canCheck"
          @toggle-sidebar="toggleSidebar"
          @open-notification="handleOpenNotification"
          @update:selected-navigation="handleNavigationUpdate"
        />
      </div>
      <Breadcrumb
        data-testid="breadcrumb"
        class="pt-4 pr-6"
      />
      <main class="flex-1 overflow-auto bg-white-02">
        <router-view />
      </main>

      <NotificationCenterModal
        v-if="isNotificationModalOpen"
        title="Atualizações"
        :notifications="recentNotices"
        :is-loading="isNoticesLoading"
        :processing-notice-id="processingNoticeId"
        @close="handleCloseNotification"
        @acknowledge="handleAcknowledgeNotice"
      />

      <Footer data-testid="footer" />
    </div>
  </div>
</template>
